import { useState, useEffect, useRef } from 'react';
import {
  Search, ChevronRight, RefreshCw, AlertCircle, CheckCircle2, X, Wifi
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';

const POLL_MS = 2000;

const DiscoveryScanStep = ({ onNext, onBack }) => {
  const { savedData, markStepComplete } = useSiteSetupStore();

  const [phase, setPhase] = useState('idle'); // idle | scanning | complete | error
  const [progress, setProgress] = useState({ phase: 0, totalPhases: 0, phaseName: '', devicesFound: 0 });
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const agentId = savedData?.agentId;
  const networks = savedData?.networks || [];

  useEffect(() => {
    if (!agentId || networks.length === 0) {
      setError('Missing network configuration. Please go back to the previous step.');
      return;
    }
    initStep();
    return () => stopPolling();
    // eslint-disable-next-line
  }, []);

  // Decide on mount whether to rescan or show existing results
  const initStep = async () => {
    const alreadyRan = (savedData?.deviceCount || 0) > 0;
    try {
      const res = await fetchWithTenant('/api/md/devices');
      if (res.ok) {
        const data = await res.json();
        const list = data.devices || data || [];
        if (list.length > 0 || alreadyRan) {
          setDevices(list);
          setPhase('complete');
          return;
        }
      }
    } catch (err) {
      console.error('[DiscoveryScan] device precheck failed:', err);
    }
    startScan();
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startScan = async () => {
    setError(null);
    setDevices([]);
    setPhase('scanning');
    try {
      const res = await fetchWithTenant('/api/md/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId, networks }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to start scan');
      }
      pollRef.current = setInterval(pollStatus, POLL_MS);
      pollStatus();
    } catch (err) {
      console.error('[DiscoveryScan] start failed:', err);
      setError(err.message || 'Failed to start scan');
      setPhase('error');
    }
  };

  const pollStatus = async () => {
    try {
      const [statusRes, devicesRes] = await Promise.all([
        fetchWithTenant('/api/md/scan/status'),
        fetchWithTenant('/api/md/devices'),
      ]);
      if (!statusRes.ok) return;
      const data = await statusRes.json();

      let liveCount = 0;
      if (devicesRes.ok) {
        const dData = await devicesRes.json();
        const list = dData.devices || dData || [];
        liveCount = list.length;
        setDevices(list);
      }

      setProgress({
        phase: data.phase || 0,
        totalPhases: data.totalPhases || 0,
        phaseName: data.phaseName || '',
        devicesFound: Math.max(
          data.devicesFound || 0,
          data.foundDevices || 0,
          liveCount
        ),
      });

      if (data.status === 'complete' || data.status === 'idle') {
        stopPolling();
        await loadDevices();
        setPhase('complete');
      }
    } catch (err) {
      console.error('[DiscoveryScan] poll failed:', err);
    }
  };

  const loadDevices = async () => {
    try {
      const res = await fetchWithTenant('/api/md/devices');
      if (!res.ok) return;
      const data = await res.json();
      setDevices(data.devices || data || []);
    } catch (err) {
      console.error('[DiscoveryScan] load devices failed:', err);
    }
  };

  const handleCancel = async () => {
    try {
      await fetchWithTenant('/api/md/scan/cancel', { method: 'POST' });
    } catch (err) {
      console.error('[DiscoveryScan] cancel failed:', err);
    }
    stopPolling();
    await loadDevices();
    setPhase('complete');
  };

  const handleNext = async () => {
    await markStepComplete('discovery_scan', {
      deviceCount: devices.length,
      deviceIds: devices.map(d => d.id),
    });
    onNext();
  };

  const pct = progress.totalPhases > 0
    ? Math.min(100, Math.round((progress.phase / progress.totalPhases) * 100))
    : 0;

  return (
    <div className="site-setup-step discovery-scan-step">
      <h3>Discover Devices</h3>
      <p className="step-description">
        Scanning {networks.join(', ')} for cameras, NVRs, switches, and other devices on your network.
      </p>

      {error && (
        <div className="pairing-code-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {phase === 'scanning' && (
        <div className="scan-progress-card">
          <div className="scan-progress-header">
            <RefreshCw size={18} className="spin" />
            <div className="scan-progress-text">
              <strong>{progress.phaseName || 'Starting scan...'}</strong>
              <span>
                Phase {progress.phase || 0} of {progress.totalPhases || '?'}
                {' · '}
                Found {progress.devicesFound} device{progress.devicesFound === 1 ? '' : 's'}
              </span>
            </div>
            <button className="site-setup-btn ghost" onClick={handleCancel}>
              <X size={14} /> Cancel
            </button>
          </div>
          <div className="scan-progress-bar">
            <div className="scan-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {phase === 'complete' && (
        <>
          <div className={`scan-summary ${devices.length > 0 ? 'success' : 'empty'}`}>
            {devices.length > 0 ? (
              <>
                <CheckCircle2 size={20} />
                <strong>Found {devices.length} device{devices.length === 1 ? '' : 's'} on your network.</strong>
              </>
            ) : (
              <>
                <Wifi size={20} />
                <div>
                  <strong>No devices found.</strong>
                  <p>
                    Verify the subnet is reachable from your agent, the agent is ONLINE,
                    and that your firewall allows ICMP/ARP/SNMP. Then try again.
                  </p>
                </div>
              </>
            )}
          </div>

          {devices.length > 0 && (
            <div className="device-results-table-wrap">
              <table className="device-results-table">
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>Hostname</th>
                    <th>Manufacturer</th>
                    <th>Type</th>
                    <th>MAC</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.slice(0, 50).map(d => (
                    <tr key={d.id}>
                      <td>{d.ipAddress || '—'}</td>
                      <td>{d.hostname || '—'}</td>
                      <td>{d.manufacturer || '—'}</td>
                      <td>{d.deviceType || 'UNKNOWN'}</td>
                      <td className="mono">{d.macAddress || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {devices.length > 50 && (
                <p className="step-hint">Showing first 50 of {devices.length} devices.</p>
              )}
            </div>
          )}
        </>
      )}

      <div className="step-actions">
        {phase === 'error' && (
          <button className="site-setup-btn secondary" onClick={startScan}>
            <RefreshCw size={14} /> Retry
          </button>
        )}
        {phase === 'complete' && devices.length === 0 && (
          <button className="site-setup-btn secondary" onClick={startScan}>
            <RefreshCw size={14} /> Scan Again
          </button>
        )}
        <button
          className="site-setup-btn primary"
          onClick={handleNext}
          disabled={phase === 'scanning'}
        >
          Continue <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default DiscoveryScanStep;
